import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PersonId, SubAreaId } from '@shared/brand';
import { subAreaColor } from '@domain/entities/Organization';
import type { CalendarDate } from '@domain/value-objects/CalendarDate';
import { getServices } from '@infrastructure/services';
import {
  Avatar,
  Banner,
  Card,
  Chip,
  EmptyState,
  Pagination,
  Spinner,
  cssVars,
  cx,
} from '../../design-system';
import { usePagination } from '../../hooks/usePagination';
import { formatDay } from '../../format';
import { useAreaName } from '../../hooks/useAreaName';
import { liveBalanceRefsFor, liveBalancesByPerson } from '../../balancePeriod';
import { useSession } from '../../providers/SessionProvider';
import { useScopedAreaId } from '../../providers/AreaFocusProvider';
import { byRemainingDaysDesc, toPersonBalanceRow } from './personRows';
import styles from './ReportsScreen.module.css';
import { loadFailed } from '../../loadError';
import { usePersonLink } from '../../hooks/usePersonLink';
import { PageTitle } from '../../shell/PageTitle';

/**
 * Reportes del área — transcribed from
 * `design/reference/screens/reportes-del-area.html`.
 *
 * Period KPIs, days per month, usage per sub area and a per-person table.
 *
 * En la tabla, el rojo marca **los días que se pierden al cerrar el periodo**, no
 * un saldo alto. Un saldo grande es un pasivo contable y se puede pagar; unos días
 * que prescriben el viernes se pierden y no vuelven, y son sobre los que se puede
 * hacer algo hoy. Ordenada de más días disponibles a menos, que es el orden en que
 * RR.HH. va a llamar a la gente. Ver `personRows.ts`.
 */

const MONTHS = [
  'ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN',
  'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC',
] as const;

export const ReportsScreen = (): ReactNode => {
  const openPerson = usePersonLink();
  const session = useSession();
  /* El área que toca mirar: la propia, o la enfocada si su alcance es la
     empresa entera. Ver `AreaFocusProvider`. */
  const areaId = useScopedAreaId(session.areaId);
  const areaName = useAreaName();
  const [subAreaFilter, setSubAreaFilter] = useState<SubAreaId | 'all'>('all');
  const today = getServices().clock.today();
  const year = today.year;

  const subAreas = useQuery({
    queryKey: ['reports', 'subAreas', areaId],
    queryFn: async () => {
      const result = await getServices().organization.listSubAreas(areaId);
      return result.ok ? result.value : [];
    },
  });

  const requests = useQuery({
    queryKey: ['reports', 'requests', areaId, year],
    queryFn: async () => {
      const result = await getServices().requests.list(
        { areaId: areaId, statuses: ['Aprobada', 'Tomada'] },
        { limit: 200 },
      );
      if (!result.ok) throw loadFailed('No pudimos cargar los reportes.', result.error);
      return result.value.items.filter((r) => r.range.start.year === year);
    },
  });

  /** Upcoming absences, for the table's "próxima ausencia" column. */
  const upcoming = useQuery({
    queryKey: ['reports', 'upcoming', areaId],
    queryFn: async () => {
      const result = await getServices().requests.list(
        { areaId: areaId, statuses: ['Aprobada', 'Visto bueno', 'En revisión'] },
        { limit: 200 },
      );
      if (!result.ok) return new Map<PersonId, CalendarDate>();

      const next = new Map<PersonId, CalendarDate>();
      for (const request of result.value.items) {
        if (request.range.start.isBefore(today)) continue;
        const current = next.get(request.personId);
        if (current === undefined || request.range.start.isBefore(current)) {
          next.set(request.personId, request.range.start);
        }
      }
      return next;
    },
  });

  const people = useQuery({
    queryKey: ['reports', 'people', areaId],
    queryFn: async () => {
      const result = await getServices().people.list(
        { areaId: areaId, status: 'active' },
        { limit: 200 },
      );
      return result.ok ? result.value.items : [];
    },
  });

  const balances = useQuery({
    queryKey: [
      'reports',
      'balances',
      areaId,
      year,
      (people.data ?? []).length,
      session.config.rules.expiryMonths,
    ],
    enabled: (people.data ?? []).length > 0,
    queryFn: async () => {
      /* Cada quien con su periodo: en un área de cuarenta personas hay tantos
         años vivos como meses de ingreso distintos. */
      const hoy = getServices().clock.today();
      const expiryMonths = session.config.rules.expiryMonths;
      const roster = people.data ?? [];
      const result = await getServices().balances.getMany(
        liveBalanceRefsFor(roster, hoy, expiryMonths),
      );
      if (!result.ok) return [];
      const byPerson = liveBalancesByPerson(roster, result.value, hoy, expiryMonths);
      return roster.flatMap((person) => {
        const row = toPersonBalanceRow(byPerson.get(person.id) ?? [], person.hireDate, hoy);
        return row === null ? [] : [row];
      });
    },
  });

  const allRequests = requests.data ?? [];
  const scoped = allRequests.filter(
    (r) => subAreaFilter === 'all' || r.subAreaId === subAreaFilter,
  );

  /** Days taken per month, for the column chart. */
  const perMonth = useMemo(() => {
    const totals = new Array<number>(12).fill(0);
    for (const request of scoped) {
      const index = request.range.start.month - 1;
      totals[index] = (totals[index] ?? 0) + request.businessDays;
    }
    return totals;
  }, [scoped]);

  const maxMonth = Math.max(1, ...perMonth);

  /** Usage per sub area, for the horizontal bars. */
  const perSubArea = useMemo(() => {
    const totals = new Map<string, number>();
    for (const request of scoped) {
      const key = request.subAreaName;
      totals.set(key, (totals.get(key) ?? 0) + request.businessDays);
    }
    return [...totals.entries()].sort((a, b) => b[1] - a[1]);
  }, [scoped]);

  const subAreaTotal = perSubArea.reduce((sum, [, value]) => sum + value, 0);
  const maxSubArea = Math.max(1, ...perSubArea.map(([, value]) => value));

  const totalDays = scoped.reduce((sum, r) => sum + r.businessDays, 0);
  const allBalances = balances.data ?? [];

  const peopleById = new Map((people.data ?? []).map((p) => [p.id, p]));
  const subAreaByName = new Map((subAreas.data ?? []).map((s) => [s.name, s]));

  /*
   * Copia antes de ordenar.
   *
   * `balances.data` es la caché de react-query, y `sort` ordena en el sitio:
   * ordenarla directamente mutaría el arreglo que otras pantallas ya tienen leído
   * — el mismo objeto, no una copia — y lo haría durante el render.
   *
   * Sin `useMemo` a propósito: dependería de `peopleById`, que es un `Map` nuevo en
   * cada render, así que el memo no ahorraría nada y sólo escondería eso.
   */
  const visibleBalances = [
    ...(subAreaFilter === 'all'
      ? allBalances
      : allBalances.filter((b) => peopleById.get(b.personId)?.subAreaId === subAreaFilter)),
  ].sort(byRemainingDaysDesc);

  /* La tabla, de diez en diez. Los cuatro números de arriba y las dos gráficas
     siguen saliendo del área entera: paginar cambia qué renglones se ven, no de
     quién se habla. */
  const balancePager = usePagination(visibleBalances, subAreaFilter);

  const scopeLabel =
    subAreaFilter === 'all'
      ? 'todas las sub áreas'
      : ((subAreas.data ?? []).find((s) => s.id === subAreaFilter)?.name ?? 'sub área');

  /**
   * The four figures the design puts here, from the prototype's `repKpis`.
   *
   * «Aprobación media» is the one that is not a count: it is how long a person
   * waits between sending and hearing back, which is the only number on this
   * screen that measures the *leader* rather than the team.
   */
  const decidedDurations = allRequests
    .filter((r) => r.decidedAt !== null)
    .map((r) => (r.decidedAt?.getTime() ?? 0) - r.createdAt.getTime())
    .filter((ms) => ms >= 0);

  const averageDecisionDays =
    decidedDurations.length === 0
      ? null
      : decidedDurations.reduce((sum, ms) => sum + ms, 0) /
        decidedDurations.length /
        86_400_000;

  const unusedBalance = visibleBalances.reduce((sum, b) => sum + b.remainingDays, 0);

  const kpis: readonly {
    label: string;
    value: number | string;
    chip: string;
    note: string;
    color: string;
  }[] = [
    {
      label: 'Días tomados',
      value: totalDays,
      chip: 'Periodo',
      note: 'ausencias registradas en el periodo',
      color: '#4a6fa5',
    },
    {
      label: 'Saldo sin usar',
      value: unusedBalance,
      chip: 'Riesgo',
      note: 'acumulado del equipo visible',
      color: '#e8930c',
    },
    {
      label: 'Aprobación media',
      // A comma, not a period: es-MX formats decimals with a period but the
      // design writes «1,8 d», and the design is what the client signed off on.
      value:
        averageDecisionDays === null
          ? '—'
          : `${averageDecisionDays.toFixed(1).replace('.', ',')} d`,
      chip: 'Líder',
      note: 'desde el envío hasta la decisión',
      color: '#12a594',
    },
    {
      label: 'Solicitudes',
      value: allRequests.length,
      chip: 'Total',
      note: 'registradas en el área',
      color: '#7c5cff',
    },
  ];

  const loading = requests.isLoading || people.isLoading;

  return (
    <div className="flow">
      <header className={styles.header}>
        <div>
          <PageTitle className={styles.title}>Reportes del área</PageTitle>
          <p className={styles.note}>
            {areaName} · periodo {year} · {scopeLabel}
          </p>
        </div>

        {(subAreas.data ?? []).length > 0 && (
          <div className={styles.scopeTrack} role="group" aria-label="Filtrar por sub área">
            <button
              type="button"
              className={cx(styles.scopeChip, subAreaFilter === 'all' && styles.scopeChipActive)}
              aria-pressed={subAreaFilter === 'all'}
              onClick={() => {
                setSubAreaFilter('all');
              }}
            >
              Todas
              <span className={styles.scopeCount}>{allRequests.length}</span>
            </button>
            {(subAreas.data ?? []).map((sa) => (
              <button
                key={sa.id}
                type="button"
                className={cx(styles.scopeChip, subAreaFilter === sa.id && styles.scopeChipActive)}
                style={cssVars({ '--scope-color': subAreaColor(sa) })}
                aria-pressed={subAreaFilter === sa.id}
                onClick={() => {
                  setSubAreaFilter(sa.id);
                }}
              >
                {sa.name}
                <span className={styles.scopeCount}>
                  {allRequests.filter((r) => r.subAreaId === sa.id).length}
                </span>
              </button>
            ))}
          </div>
        )}
      </header>

      {loading ? (
        <Card>
          <Spinner label="Calculando" />
        </Card>
      ) : requests.error !== null ? (
        <Banner tone="danger">No pudimos cargar los reportes.</Banner>
      ) : (
        <>
          <section className={styles.panel}>
            <div className={styles.panelHead}>
              <h2 className={styles.panelTitle}>Resumen del periodo</h2>
              <span className={styles.panelMeta}>
                {scopeLabel} · {year}
              </span>
            </div>

            <div className={styles.kpiBand}>
              {kpis.map((kpi) => (
                <div key={kpi.label} className={styles.kpiCell}>
                  <div className={styles.kpiHead}>
                    <span
                      className={styles.kpiDot}
                      style={cssVars({ '--kpi-color': kpi.color })}
                    />
                    <span className={styles.kpiLabel}>{kpi.label}</span>
                  </div>
                  <div className={styles.kpiFigure}>
                    <span className={styles.kpiValue}>{kpi.value}</span>
                    <Chip
                      background={`color-mix(in srgb, ${kpi.color} 12%, transparent)`}
                      foreground={kpi.color}
                    >
                      {kpi.chip}
                    </Chip>
                  </div>
                  <div className={styles.kpiNote}>{kpi.note}</div>
                </div>
              ))}
            </div>
          </section>

          <div className={styles.pair}>
            <section className={styles.panel}>
              <div className={styles.panelHead}>
                <h2 className={styles.panelTitle}>Días de ausencia por mes</h2>
                <span className={styles.panelMeta}>{totalDays} días en el periodo</span>
              </div>

              {totalDays === 0 ? (
                <EmptyState glyph="≡" title="Sin días tomados todavía en este periodo" />
              ) : (
                <div className={cx(styles.chartScroll, 'scroll-x')}>
                  <div className={styles.chart}>
                    {perMonth.map((value, index) => (
                      <div key={MONTHS[index]} className={styles.chartColumn}>
                        <span className={styles.chartValue}>{value > 0 ? value : ''}</span>
                        <span
                          className={cx(styles.chartBar, value === 0 && styles.chartBarEmpty)}
                          style={{
                            height: `${String(Math.max(2, (value / maxMonth) * 100))}%`,
                          }}
                        />
                        <span
                          className={cx(
                            styles.chartLabel,
                            index === today.month - 1 && styles.chartLabelCurrent,
                          )}
                        >
                          {MONTHS[index]}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            <section className={styles.panel}>
              <div className={styles.panelHead}>
                <h2 className={styles.panelTitle}>Uso por sub área</h2>
              </div>

              {perSubArea.length === 0 ? (
                <EmptyState glyph="▤" title="Sin datos por sub área" />
              ) : (
                <div className={styles.bars}>
                  {perSubArea.map(([name, value]) => {
                    const color = subAreaColor(subAreaByName.get(name));
                    const share =
                      subAreaTotal === 0 ? 0 : Math.round((value / subAreaTotal) * 100);
                    return (
                      <div key={name}>
                        <div className={styles.barHead}>
                          <span className={styles.barName}>
                            <span
                              className={styles.barDot}
                              style={cssVars({ '--bar-color': color })}
                            />
                            {name}
                          </span>
                          <span className={styles.barValue}>
                            {value} días · {share}%
                          </span>
                        </div>
                        <div
                          className={styles.barTrack}
                          role="img"
                          aria-label={`${name}: ${String(value)} días, ${String(share)}%`}
                        >
                          <div
                            className={styles.barFill}
                            style={cssVars({
                              '--bar-color': color,
                              width: `${String((value / maxSubArea) * 100)}%`,
                            })}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>

          <section className={styles.panel}>
            <div className={styles.panelHead}>
              <h2 className={styles.panelTitle}>Detalle por persona</h2>
              <span className={styles.panelMeta}>
                De más saldo a menos · en rojo, los días que están por vencer
              </span>
            </div>

            {visibleBalances.length === 0 ? (
              <EmptyState glyph="·" title="Sin saldos calculados" />
            ) : (
              <div className={cx(styles.tableScroll, 'scroll-x')}>
                <div className={styles.tableGrid}>
                  <div className={styles.tableHead}>
                    <div className={styles.tableHeadCell}>Persona</div>
                    <div className={styles.tableHeadCell}>Sub área</div>
                    <div className={styles.tableHeadCell}>Tomados</div>
                    <div className={styles.tableHeadCell}>Saldo sin usar</div>
                    <div className={styles.tableHeadCell}>Próxima ausencia</div>
                  </div>

                  {balancePager.items.map((row) => {
                    const person = peopleById.get(row.personId);
                    const sa = (subAreas.data ?? []).find((s) => s.id === person?.subAreaId);
                    const color = subAreaColor(sa);
                    const expiring = row.expiring !== null;
                    const next = upcoming.data?.get(row.personId);
                    const ratio =
                      row.entitledDays === 0
                        ? 0
                        : Math.min(100, (row.remainingDays / row.entitledDays) * 100);

                    return (
                      <div key={row.id} className={styles.tableRow}>
                        <div className={styles.tablePerson}>
                          <Avatar
                            name={row.personName}
                            color={color}
                            size={32}
                            onOpen={openPerson(row.personId)}
                          />
                          <span className={styles.tableName}>{row.personName}</span>
                        </div>
                        <span className={styles.tableSub}>{sa?.name ?? 'Sin sub área'}</span>
                        <span className={styles.tableTaken}>{row.takenDays}</span>
                        <div className={styles.tableBalance}>
                          <div className={styles.balanceRow}>
                            <div className={styles.miniTrack}>
                              <div
                                className={cx(styles.miniFill, expiring && styles.miniFillRisk)}
                                style={{ width: `${String(ratio)}%` }}
                              />
                            </div>
                            <span
                              className={cx(
                                styles.balanceValue,
                                expiring && styles.balanceValueRisk,
                              )}
                            >
                              {row.remainingDays}
                            </span>
                          </div>
                          {/* Cuándo se pierden y cuántos. Sin la fecha, el rojo dice que
                              pasa algo pero no cuánto margen queda, que es lo único que
                              cambia lo que RR.HH. hace esta semana. La cifra va aparte
                              porque ya no es la de al lado: con los periodos encimados
                              vence lo del periodo viejo, no el saldo entero. */}
                          {row.expiring !== null && (
                            <span className={styles.balanceExpiry}>
                              {row.expiring.days} vencen el {formatDay(row.expiring.lastValidDay)}
                            </span>
                          )}
                        </div>
                        <span className={styles.tableNext}>
                          {next === undefined ? 'Sin agendar' : formatDay(next)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <Pagination pager={balancePager} noun="personas" />
          </section>
        </>
      )}
    </div>
  );
};
